import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertTriangle, CheckCircle2, Download, FileJson, FileSpreadsheet } from 'lucide-react'
import type { DashboardStats } from '../hooks/useDashboardStats'

interface ZeroResultsProps {
  queries: DashboardStats['zeroResultQueries'] | undefined
  isLoading: boolean
}

export function ZeroResults({ queries, isLoading }: ZeroResultsProps) {
  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async (format: 'csv' | 'json') => {
    setIsExporting(true)
    try {
      const response = await fetch(`/api/stats/export?type=zero_results&format=${format}`, {
        credentials: 'include',
      })

      if (!response.ok) throw new Error('Export failed')

      if (format === 'csv') {
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `zero-result-queries-${new Date().toISOString().split('T')[0]}.csv`
        a.click()
        URL.revokeObjectURL(url)
      } else {
        const data = await response.json()
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `zero-result-queries-${new Date().toISOString().split('T')[0]}.json`
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Zero-Result Queries
            </CardTitle>
            <CardDescription>Searches that found no products</CardDescription>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" disabled={isExporting || isLoading}>
                <Download className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('json')}>
                <FileJson className="mr-2 h-4 w-4" />
                Export JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-10" />
              </div>
            ))}
          </div>
        ) : queries && queries.length > 0 ? (
          <>
            <div className="space-y-3">
              {queries.slice(0, 10).map((item) => (
                <div
                  key={item.query}
                  className="flex items-center justify-between p-2 rounded-lg bg-amber-500/10"
                >
                  <span className="text-sm font-medium">"{item.query}"</span>
                  <span className="text-sm text-muted-foreground">
                    {item.count.toLocaleString()} searches
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Consider adding products for these search terms or creating redirects
            </p>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle2 className="h-10 w-10 text-green-500/50 mb-2" />
            <p className="text-sm text-muted-foreground">All clear!</p>
            <p className="text-xs text-muted-foreground mt-1">
              No zero-result queries to report
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
